/**
 * @prettier
 * @flow
 */
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import { useRef } from 'react';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import type { AutoCompleteInputProp, Variant } from 'src/components/form/FormTypes';
import { transformStringInput } from 'src/components/form/utils/transformStringInput';
import { transformStringOutput } from 'src/components/form/utils/transformStringOutput';
import { getError } from 'src/components/form/utils/getError';
import { translate } from 'src/utils/translate/translate';

export function FormCurrencyNumberStringField({ name, label, placeholder, helperText, defaultValue = null, autoComplete = 'off', disabled, required, min, max, variant = 'outlined', InputProps, inputProps}: Props) {

    const { errors, control, formState: { isSubmitting } } = useFormContext();
    const inputRef = useRef();

    return (
        <Controller
            render={({ value, onChange, ...props }) => (
                <TextField
                    {...props}
                    value={transformStringInput(value)}
                    onChange={(value) => onChange(transformStringOutput(value))}
                    label={label ?? translate('Amount') }
                    placeholder={placeholder}
                    helperText={getError(errors, name)?.message ?? helperText}
                    disabled={isSubmitting || disabled}
                    required={required}
                    error={!!getError(errors, name)}
                    variant={variant}
                    inputRef={inputRef}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        ...InputProps,
                    }}
                    inputProps={{ autoComplete, ...inputProps }}
                    InputLabelProps={{ shrink: placeholder ? true : undefined }}
                    fullWidth
                />
            )}
            control={control}
            name={name}
            defaultValue={defaultValue}
            rules={{
                required: {
                    value: (required: any)?.value ?? required,
                    message: (required: any)?.message ??  translate('This field is required'),
                },
                min: {
                    value: (min: any)?.value ?? min,
                    message: (min: any)?.message ??  translate('Must be number equal to or larger than @number', { number: (min: any)?.value ?? min }),
                },
                max: {
                    value: (max: any)?.value ?? max,
                    message: (max: any)?.message ??  translate('Must be number equal to or less than @number', { number: (max: any)?.value ?? max }),
                },
            }}
            onFocus={() => { // make focus on error work when disabled={isSubmitting || disabled}
                if(inputRef.current) {
                    inputRef.current.disabled = false;
                    inputRef.current.focus();
                }
            }}
        />
    );
}

export function NumberFormatCustom(props: {
    inputRef: any,
    name: string,
    onChange: Function,
}) {
    const { inputRef, onChange, ...other } = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => onChange(values.value)}
            // allowedDecimalSeparators={[',', '.']}
            thousandSeparator
            isNumericString
            decimalScale={2}
            // prefix="$"
        />
    );
}

type Props = {|
    name: string,
    label?: string,
    placeholder?: string,
    helperText?: string,
    defaultValue?: string,
    autoComplete?: AutoCompleteInputProp,
    disabled?: boolean,
    required?: boolean | { value: number, message: string },
    min?: number | { message: string, value: number },
    max?: number | { message: string, value: number },
    variant?: Variant,
    InputProps?: any,
    inputProps?: any,
|};

